import React, { useContext, useEffect, useState } from 'react';
import { getReceiptLink } from '../../stores/profile.store';
import { IPaymentStatus, IUserPayments, IWebAuthData } from '../../interfaces';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { MainContext } from '../../context/main.context';
import { copyToClipboard, getAuthElemStyle, getUserAuthData, getWebAuthData } from '../../common/tools';
import { GET_PROFILE_INFO_ERROR } from '../../common/errorCodes';
import Header from '../header';
import {
    PaymentIdCopySvg,
    ArrowDownPaymentSvg,
    ArrowUpPaymentSvg,
    ReceiptSvg,
    InfoSvg,
    IsTimeInfoSvg,
} from '../../icons';
import Footer from '../footer';
import ManualController from '../../hoc/ManualController';
import { getUserPayments } from '../../stores/payment.store';
import { NO_PURCHASES_LOGO } from '../../common/constants';
import Modal from '../modal/Modal';
import TransactionHelper from './helper/TransactionHelper';


export default function Transactions() {
    const { userAuthData, project, settings, modalContent, loader, toggleLoader, setModalContent } = useContext(MainContext);
    const { manualRedirect } = ManualController();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [ userPayments, setUserPayments ] = useState<IUserPayments[]>([]);
    const [ userData, setUserData ] = useState<IWebAuthData|null>(null);
    const [ selectedPaymentId, setSelectedPaymentId ] = useState<string>('');
    const [ showReceiptGenerationModal, setShowReceiptGenerationModal ] = useState<boolean>(false);

    useEffect((): void => {
        if (!userAuthData) {
            navigate('/');
            return;
        }

        const webAuthData: IWebAuthData | null = getUserAuthData();

        if (webAuthData) {
            setUserData(webAuthData);
        }

        _getUserPayments();
    }, []);

    const _getUserPayments = (): void => {
        toggleLoader(true);

        getUserPayments(project).then(res => {
            const result: IUserPayments[] = res?.data;

            if (result) {
                setUserPayments(result);
                return;
            }

            manualRedirect(res, 'auth', true, GET_PROFILE_INFO_ERROR);
        }, err => {
            const errData = err?.response?.data || err;
            manualRedirect(errData, 'auth', true, GET_PROFILE_INFO_ERROR);
        }).finally((): void => {
            toggleLoader(false);
        });
    }

    const _onPaymentIdClick = (uuid: string)=> {
        copyToClipboard(uuid, t('payment.copy'));
    }

    const _onReceiptClick = (id: string): void => {
        _getReceiptLink(id);
    }

    const _getReceiptLink = (id: string): void => {
        getReceiptLink(id).then(res => {
            const receiptLink: string = res?.data?.result;

            if (!receiptLink) {
                setShowReceiptGenerationModal(true);
                return;
            }

            window.open(receiptLink, '_blank');
        }, err => {
            const errData = err?.response?.data || err;
            console.error('GET_RECEIPT_LINK_ERROR', { errData });
            toast.error('GET_RECEIPT_LINK_ERROR');
        });
    }

    const _renderCurrency = (price: string, currency: string) => {
        const code: string = currency === 'RUB' ? String.fromCharCode(8381) : String.fromCharCode(36);

        return (
            <div className="transactions-body-item-right-payment-data-price">{`${price} ${code}`}</div>
        );
    };

    const _renderPaymentsStatus = (status: IPaymentStatus) => {
        let statusValue: string = '';
        let color: string = ''

        if (status === 'pending') {
            statusValue = t('payment.status.pending');
            color = '#E88D3A';
        } else if (status === 'paid') {
            statusValue = t('payment.status.paid');
            color = '#259D58';
        } else if (status === 'none') {
            statusValue = t('payment.status.none');
            color = '#B01E1E';
        } else if (status === 'fail') {
            statusValue = t('payment.status.fail');
            color = '#B01E1E';
        }

        return (
            <div style={{color}}>
                {statusValue}
            </div>
        );
    }

    const _renderUserPayments = () => {
        if (loader) {
            return null;
        }

        if (!userPayments?.length) {
            return (
                <div className="transactions-body-stub auth-modal-transactions">
                    <div className="transactions-body-stub-container">
                        <img className="games-header-logo-img" alt="" src={NO_PURCHASES_LOGO}/>
                    </div>
                    <div className="transactions-body-stub-container-title">
                        {t('support.noPurchases')}
                    </div>
                </div>
            );
        }

        return (
            <div className="transactions-body-container">
                {
                    userPayments.map((payment: IUserPayments, index: number) => {
                        const { createTs, title, price, currency, status, uuid, projectName } = payment;

                        const key: string = `${uuid}_${index}`;
                        const date: string = createTs ? `${new Date(createTs).toISOString().slice(0, 10)}` : '';

                        if (selectedPaymentId === uuid) {
                            return (
                                <div key={key} className="transactions-body-item-open">
                                    <div
                                        className="transactions-body-item-open-header"
                                        onClick={() => setSelectedPaymentId('')}
                                    >
                                        <div className="transactions-body-item-left">
                                            <div className="transactions-body-item-left-date">
                                                {date}
                                            </div>
                                            <div className="transactions-body-item-left-payment-name">
                                                {title}
                                            </div>
                                        </div>
                                        <div className="transactions-body-item-center">
                                            <div className="transactions-body-item-right-payment-data">
                                                <div
                                                    className="transactions-body-item-right-payment-data-status">
                                                    {_renderPaymentsStatus(status)}
                                                </div>
                                                {_renderCurrency(price, currency)}
                                            </div>
                                        </div>
                                        <div className="transactions-body-item-right">
                                            <ArrowDownPaymentSvg/>
                                        </div>
                                    </div>
                                    <div className="transactions-body-item-open-payment">
                                        <div className="transactions-body-item-open-payment-id-title">
                                            ID
                                        </div>
                                        <div className="transactions-body-item-open-payment-id">
                                            <div className="transactions-body-item-open-payment-id-value">
                                                {uuid}
                                            </div>
                                            <div className="transactions-body-item-open-payment-id-extra">
                                                <div
                                                    className="transactions-body-item-open-payment-id-copy"
                                                    onClick={() => _onPaymentIdClick(uuid)}
                                                >
                                                    <PaymentIdCopySvg/>
                                                </div>
                                                <div
                                                    className="transactions-body-item-open-payment-id-copy"
                                                    onClick={() => _onReceiptClick(uuid)}
                                                >
                                                    <ReceiptSvg/>
                                                </div>
                                                <div
                                                    className="transactions-body-item-open-payment-id-copy"
                                                    onClick={() => {
                                                        if (modalContent) {
                                                            const paramsObject = {
                                                                id: uuid
                                                            }

                                                            setModalContent({ component: <TransactionHelper/>, values: paramsObject });
                                                            return;
                                                        }

                                                        navigate(`/transactions/help?id=${uuid}`);
                                                    }}
                                                >
                                                    <InfoSvg/>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="transactions-body-item-open-payment-data">
                                        <div className="transactions-body-item-open-payment-data-container">
                                            <div
                                                className="transactions-body-item-open-payment-data-container-title">
                                                {t('profile.projectName')}
                                            </div>
                                            <div
                                                className="transactions-body-item-open-payment-data-container-value">
                                                {projectName}
                                            </div>
                                        </div>
                                        <div className="transactions-body-item-open-payment-data-container">
                                            <div
                                                className="transactions-body-item-open-payment-data-container-title">
                                            {t('payment.paymentMethod')}
                                            </div>
                                            <div
                                                className="transactions-body-item-open-payment-data-container-value">
                                                {t('payment.debitCard')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={key} className="transactions-body-item" onClick={() => setSelectedPaymentId(uuid)}>
                                <div className="transactions-body-item-left">
                                    <div className="transactions-body-item-left-date text-overflow">
                                        {date}
                                    </div>
                                    <div className="transactions-body-item-left-payment-name text-overflow">
                                        {title}
                                    </div>
                                </div>
                                <div className="transactions-body-item-center">
                                    <div className="transactions-body-item-right-payment-data">
                                        <div className="transactions-body-item-right-payment-data-status">
                                            {_renderPaymentsStatus(status)}
                                        </div>
                                        <div className="transactions-body-item-right-payment-data-price">
                                            {_renderCurrency(price, currency)}
                                        </div>
                                    </div>
                                </div>
                                <div className="transactions-body-item-right">
                                    <ArrowUpPaymentSvg/>
                                </div>
                            </div>
                        );
                    })
                }
            </div>
        );
    };

    const _renderReceiptGenerationModal = () => {
        return (
            <div className="transaction-modal">
                <div className="transaction-modal-title">
                    <div className="transaction-modal-title-img">
                        <IsTimeInfoSvg/>
                    </div>
                    <div>
                        {t('receipt.generation')}
                    </div>
                </div>
                <div className="transaction-modal-text">
                    {t('receipt.wait')}
                </div>
                <button className="transaction-modal-button" onClick={() => setShowReceiptGenerationModal(false)}>
                    OK
                </button>
            </div>
        );
    }

    return (
        <div className="auth" id="auth-element-id" style={{background: !modalContent ? '#e8e8e8' : 'unset'}}>
            <div
                className="auth-container"
                style={{
                    background: !modalContent ? '#e8e8e8' : 'unset',
                    minHeight: !modalContent ? 'calc(100vh - 100px)' : '100%',
                }}
            >
                <div className="auth-modal" style={getAuthElemStyle(settings)}>
                    <Header origin="transactions" userData={userData}/>
                    {_renderUserPayments()}
                </div>
            </div>
            <Modal hideCloseButton open={showReceiptGenerationModal}>
                {_renderReceiptGenerationModal()}
            </Modal>
            {!modalContent && <Footer/>}
        </div>
    );
}